const TOTAL_ASSETS_PATTERN =
  /total\s+(?:current\s+)?assets[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i;
const TOTAL_LIABILITIES_PATTERN =
  /total\s+(?:current\s+)?liabilit(?:y|ies)[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i;
const SAFE_PATTERNS = [
  /\bSAFE(?:\s+notes?)?[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i,
  /(?:minus|less|excluding|subtracted)\s+SAFE[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i,
  /SAFE[^$\d-]*(?:of|:)\s*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i,
  /liabilit(?:y|ies)[^$\d-]*-\s*SAFE[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i,
];
const DEBT_PATTERN = /total\s+(?:outstanding\s+)?debt[^$\d-]*\$?\s*([\d,]+(?:\.\d+)?)(?:(k|m)\b)?/i;

function parseDollarAmount(raw) {
  const normalized = String(raw || '').replace(/,/g, '').trim();
  if (!normalized) {
    return null;
  }
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function applyAmountSuffix(value, suffix) {
  if (!suffix || value == null) {
    return value;
  }
  const unit = String(suffix).toLowerCase();
  if (unit === 'k') {
    return value * 1000;
  }
  if (unit === 'm') {
    return value * 1000000;
  }
  return value;
}

function firstDollarMatch(text, pattern) {
  if (!text) {
    return null;
  }
  const match = text.match(pattern);
  if (!match) {
    return null;
  }
  const value = parseDollarAmount(match[1]);
  return applyAmountSuffix(value, match[2]);
}

function firstDollarMatchFromPatterns(text, patterns) {
  for (const pattern of patterns) {
    const value = firstDollarMatch(text, pattern);
    if (value != null) {
      return value;
    }
  }
  return null;
}

function balanceSheetSourceText(snapshot) {
  return [
    snapshot?.balanceSheet?.reason,
    snapshot?.totalLiabilities?.reason,
    snapshot?.explanation,
  ]
    .filter(Boolean)
    .join('\n');
}

function fieldMissing(field) {
  return field?.value == null;
}

function withParsedField(existing, value, reason, fallbackReason) {
  if (value == null) {
    return existing;
  }
  return {
    value,
    reason: reason || fallbackReason,
  };
}

/** Compute runway from beginning cash ÷ net burn when both are available. */
export function applyComputedAccountingRunway(snapshot) {
  if (!snapshot) {
    return snapshot;
  }
  const netBurnValue = snapshot.netBurn?.value;
  const beginCashValue = snapshot.beginCash?.value;
  const netBurn =
    netBurnValue != null && Number.isFinite(Number(netBurnValue))
      ? Number(netBurnValue)
      : null;
  const beginCash =
    beginCashValue != null && Number.isFinite(Number(beginCashValue))
      ? Number(beginCashValue)
      : null;
  if (netBurn == null || Math.abs(netBurn) <= 0 || beginCash == null) {
    return snapshot;
  }
  const burn = Math.abs(netBurn);
  const months = Math.round((beginCash / burn) * 100) / 100;
  return {
    ...snapshot,
    runway: {
      value: months,
      reason: `Beginning cash $${Math.round(beginCash).toLocaleString('en-US')} ÷ net burn $${Math.round(burn).toLocaleString('en-US')}/mo`,
    },
  };
}

function manualSnapshotField(value, reason = 'Manual entry') {
  if (value == null || !Number.isFinite(Number(value))) {
    return undefined;
  }
  return { value: Number(value), reason };
}

/** Keep accounting snapshot runway/net burn aligned with editable UW KPI fields. */
export function syncAccountingSnapshotKpis(
  snapshot,
  {
    beginCash,
    netBurn,
    runway,
    runwaySource,
    totalAssets,
    totalAssetsSource,
    totalLiabilities,
    totalLiabilitiesSource,
  } = {}
) {
  if (!snapshot) {
    return snapshot;
  }
  let next = { ...snapshot };
  if (beginCash != null) {
    const field = manualSnapshotField(beginCash);
    if (field) {
      next.beginCash = field;
    }
  }
  if (netBurn != null) {
    const field = manualSnapshotField(netBurn);
    if (field) {
      next.netBurn = field;
    }
  }
  if (totalAssetsSource === 'Manual entry') {
    const field = manualSnapshotField(totalAssets);
    if (field) {
      next.totalAssets = field;
    }
  }
  if (totalLiabilitiesSource === 'Manual entry') {
    const field = manualSnapshotField(totalLiabilities);
    if (field) {
      next.totalLiabilities = field;
    }
  }
  if (runwaySource === 'Manual entry') {
    const field = manualSnapshotField(runway);
    if (field) {
      next.runway = field;
      return next;
    }
  }
  return applyComputedAccountingRunway(next);
}

/** Fill totalAssets/totalLiabilities/safe/debt when AI used legacy balanceSheet-only extract. */
export function enrichAccountingSnapshot(snapshot) {
  if (!snapshot) {
    return snapshot;
  }
  const text = balanceSheetSourceText(snapshot);
  let next = { ...snapshot };
  if (text) {
    const sourceReason = snapshot.balanceSheet?.reason || 'Parsed from balance sheet extract';

    if (fieldMissing(next.totalAssets)) {
      next.totalAssets = withParsedField(
        next.totalAssets,
        firstDollarMatch(text, TOTAL_ASSETS_PATTERN),
        sourceReason,
        'Total assets from balance sheet extract'
      );
    }
    if (fieldMissing(next.totalLiabilities)) {
      next.totalLiabilities = withParsedField(
        next.totalLiabilities,
        firstDollarMatch(text, TOTAL_LIABILITIES_PATTERN),
        sourceReason,
        'Total liabilities from balance sheet extract'
      );
    }
    if (fieldMissing(next.safe)) {
      next.safe = withParsedField(
        next.safe,
        firstDollarMatchFromPatterns(text, SAFE_PATTERNS),
        sourceReason,
        'SAFE from balance sheet extract'
      );
    }
    if (fieldMissing(next.debt)) {
      next.debt = withParsedField(
        next.debt,
        firstDollarMatch(text, DEBT_PATTERN),
        sourceReason,
        'Debt from balance sheet extract'
      );
    }
  }

  return applyComputedAccountingRunway(next);
}
